import { TFunction } from 'i18next';
import { toLocaleDateTime } from 'utils/datetime';
import {
  getBranchRuleText,
  GitHubBranchResult,
  GitHubBranchRule,
  GitHubBranchRuleResult,
  GitHubBranchRulesConfig,
  GitHubBranchRulesEvidence,
  GitHubBranchRulesRepository,
  readBranchRulesEvidence,
} from './IntegrationConfig_GitHub_BranchRules';

//
// Branch rules (PLAN-FBL-002): a branch name (no patterns, amendment 1), and what its protection must do
//
export class BranchProtectionRule extends GitHubBranchRule {
  //the protection must also bind administrators (CIS 1.1.14)
  requireEnforcedOnAdmins: boolean = true;

  //the branch must not be deletable (CIS 1.1.17)
  requireDeletionBlocked: boolean = false;

  clone(): this {
    const rule = new BranchProtectionRule();
    rule.branch = this.branch;
    rule.requireEnforcedOnAdmins = this.requireEnforcedOnAdmins;
    rule.requireDeletionBlocked = this.requireDeletionBlocked;

    return rule as this;
  }

  isEqual(other: this): boolean {
    return (
      this.branch.trim() === other.branch.trim() &&
      this.requireEnforcedOnAdmins === other.requireEnforcedOnAdmins &&
      this.requireDeletionBlocked === other.requireDeletionBlocked
    );
  }
}

//
// Config class: the API's GitHub_BranchProtectionConfig
//
export class IntegrationConfig_GitHub_BranchProtection extends GitHubBranchRulesConfig<BranchProtectionRule> {
  defaultRules: BranchProtectionRule[] = [new BranchProtectionRule()];

  protected newRule(): BranchProtectionRule {
    return new BranchProtectionRule();
  }

  static from(stored: unknown): IntegrationConfig_GitHub_BranchProtection {
    const raw = (stored ?? {}) as {
      selection?: unknown;
      defaultRules?: unknown[] | null;
      repositoryRules?: unknown[];
      createChecklistItemPerRepository?: boolean;
      requireEnforcedOnAdmins?: boolean;
      requireDeletionBlocked?: boolean;
    };
    const config = new IntegrationConfig_GitHub_BranchProtection();
    config.readBranchRules(raw);

    if (!Array.isArray(raw.defaultRules)) {
      //a legacy config: its single branch and requirements are the one default rule, as the API reads it
      const legacy = (raw.selection ?? {}) as { branch?: string };
      config.defaultRules = [
        config.createRule({
          branch: legacy.branch ?? '',
          requireEnforcedOnAdmins: raw.requireEnforcedOnAdmins ?? true,
          requireDeletionBlocked: raw.requireDeletionBlocked ?? false,
        }),
      ];
    }

    return config;
  }
}

//The problem codes of the API's GitHub_BranchProtectionHandler, stable in the evidence
export const getBranchProtectionProblemText = (code: string, t: TFunction<string[]>): string => {
  switch (code) {
    case 'NotProtected':
      return t('integrations:GitHub_BranchProtection.Problems.NotProtected');
    case 'AdminsNotEnforced':
      return t('integrations:GitHub_BranchProtection.Problems.AdminsNotEnforced');
    case 'AdminBypassUnknown':
      return t('integrations:GitHub_BranchProtection.Problems.AdminBypassUnknown');
    case 'DeletionAllowed':
      return t('integrations:GitHub_BranchProtection.Problems.DeletionAllowed');
    case 'PlanNotSupported':
      return t('integrations:GitHub_BranchProtection.Problems.PlanNotSupported');
    case 'BranchNotFound':
      return t('integrations:GitHub_BranchProtection.Problems.BranchNotFound');
    case 'RepositoryNotFound':
      return t('integrations:GitHub_BranchProtection.Problems.RepositoryNotFound');
    default:
      return code;
  }
};

//What protects the branch: classic branch protection, a ruleset, both or none
export const getBranchProtectionSourceText = (source: string, t: TFunction<string[]>): string => {
  switch (source) {
    case 'Classic':
      return t('integrations:GitHub_BranchProtection.Source.Classic');
    case 'Ruleset':
      return t('integrations:GitHub_BranchProtection.Source.Ruleset');
    case 'Both':
      return t('integrations:GitHub_BranchProtection.Source.Both');
    default:
      return t('integrations:GitHub_BranchProtection.Source.None');
  }
};

export const getYesNoUnknownText = (value: boolean | undefined, t: TFunction<string[]>): string => {
  if (value === undefined) return t('integrations:GitHubBaseline.Unknown');

  return value ? t('translation:General.Words.Yes') : t('translation:General.Words.No');
};

//
// Evidence classes: repository > rule > branch
//
export class BranchResult extends GitHubBranchResult {
  source: string = 'None';

  classicProtectionPresent: boolean = false;

  rulesetIds: number[] = [];

  //undefined when rulesets apply, whose bypass list is not read
  enforcedOnAdmins: boolean | undefined = undefined;

  deletionBlocked: boolean = false;

  getFoundText(t: TFunction<string[]>): string {
    return t('integrations:GitHub_BranchProtection.Result.Found', {
      source: getBranchProtectionSourceText(this.source, t),
      admins: getYesNoUnknownText(this.enforcedOnAdmins, t),
    });
  }
}

export class BranchRuleResult extends GitHubBranchRuleResult<BranchResult> {
  requireEnforcedOnAdmins: boolean = false;

  requireDeletionBlocked: boolean = false;
}

export type GitHub_BranchProtectionRepository = GitHubBranchRulesRepository<BranchRuleResult>;

export class GitHub_BranchProtectionEvidence extends GitHubBranchRulesEvidence<BranchRuleResult> {
  toMarkdownReport(t: TFunction<['translation', 'integrations']>): string {
    const typedT = t as TFunction<string[]>;
    const sb: string[] = [];
    const pass = t('integrations:GitHubBaseline.Result.Pass');

    sb.push(
      `## ${t('integrations:GitHub_BranchProtection.Report.SummaryHeading', {
        organization: this.organization,
        date: toLocaleDateTime(this.evaluatedAtUtc),
      })}`,
    );
    sb.push(
      `- ${t('integrations:GitHubBaseline.Report.CountLine', {
        evaluated: this.repositoriesEvaluated,
        failed: this.repositoriesFailed,
      })}`,
    );
    if (this.isCapped) {
      sb.push(`- ${t('integrations:GitHubBaseline.Report.Capped')}`);
    }
    sb.push(
      `- ${t('integrations:GitHubBaseline.Report.OutcomeLine', {
        outcome: this.pass ? pass : t('integrations:GitHubBaseline.Result.Fail'),
      })}`,
    );
    sb.push('');
    sb.push(
      `| ${t('integrations:GitHubBaseline.Columns.Repository')} | ${t(
        'integrations:GitHubBaseline.Columns.Rule',
      )} | ${t('integrations:GitHubBaseline.Columns.Branch')} | ${t('integrations:GitHubBaseline.Columns.Found')} | ${t(
        'integrations:GitHubBaseline.Columns.Result',
      )} |`,
    );
    sb.push('| --- | --- | --- | --- | --- |');
    for (const repository of this.repositories) {
      if (repository.problems.length > 0) {
        const problems = repository.problems.map((code) => getBranchProtectionProblemText(code, typedT)).join(' ');
        sb.push(`| ${repository.name} | | | | ${problems} |`);
      }
      for (const rule of repository.rules) {
        const ruleText = getBranchRuleText(rule, typedT);
        for (const branch of rule.branches) {
          const result = branch.passed
            ? pass
            : branch.problems.map((code) => getBranchProtectionProblemText(code, typedT)).join(' ');
          sb.push(`| ${repository.name} | ${ruleText} | ${branch.name} | ${branch.getFoundText(typedT)} | ${result} |`);
        }
      }
    }

    return sb.join('\n');
  }
}

//The nested shape only (PLAN-FBL-002); a result from before it shows as "test again"
export const parse_Evidence_GitHub_BranchProtection = (
  raw: string | undefined,
): GitHub_BranchProtectionEvidence | undefined =>
  readBranchRulesEvidence(
    new GitHub_BranchProtectionEvidence(),
    raw,
    (item) => {
      const branch = new BranchResult();
      branch.source = typeof item.source === 'string' ? item.source : 'None';
      branch.classicProtectionPresent = item.classicProtectionPresent === true;
      branch.rulesetIds = Array.isArray(item.rulesetIds) ? item.rulesetIds.filter((id) => typeof id === 'number') : [];
      branch.enforcedOnAdmins = typeof item.enforcedOnAdmins === 'boolean' ? item.enforcedOnAdmins : undefined;
      branch.deletionBlocked = item.deletionBlocked === true;

      return branch;
    },
    (item) => {
      const rule = new BranchRuleResult();
      rule.requireEnforcedOnAdmins = item.requireEnforcedOnAdmins === true;
      rule.requireDeletionBlocked = item.requireDeletionBlocked === true;

      return rule;
    },
  );
